import React, { useState, useEffect, useRef } from 'react';
import { Search, X, Loader, Receipt, DollarSign } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { searchExpenses } from '../services/walletService';

/**
 * ExpenseSearchSelect — Componente para buscar y vincular gastos a movimientos bancarios (salidas).
 * Similar a OrderSearchSelect pero orientado a la tabla `expenses`.
 */
const ExpenseSearchSelect = ({ currentExpenseId, currentExpenseData, onLink, onUnlink, movementAmount }) => {
    const [isEditing, setIsEditing] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [results, setResults] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showResults, setShowResults] = useState(false);

    const wrapperRef = useRef(null);

    useEffect(() => {
        function handleClickOutside(event) {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
                setShowResults(false);
            }
        }
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [wrapperRef]);

    useEffect(() => {
        const timeoutId = setTimeout(() => {
            if (searchTerm.length >= 2) {
                doSearch();
            } else {
                setResults([]);
            }
        }, 350);
        return () => clearTimeout(timeoutId);
    }, [searchTerm]);

    const doSearch = async () => {
        setLoading(true);
        try {
            // Si el usuario busca texto, usarlo; si no, intentar buscar por monto del movimiento
            const absAmount = movementAmount ? Math.abs(movementAmount) : null;
            const data = await searchExpenses(searchTerm, absAmount);
            setResults(data?.data || []);
            setShowResults(true);
        } catch (error) {
            console.error("Error buscando gastos", error);
            setResults([]);
        } finally {
            setLoading(false);
        }
    };

    const handleSelect = async (expense) => {
        setShowResults(false);
        try {
            await onLink(expense);
            setIsEditing(false);
            setSearchTerm('');
            toast.success('Gasto vinculado exitosamente');
        } catch (e) {
            toast.error('Error vinculando gasto');
            setSearchTerm('');
        }
    };

    const handleUnlink = async () => {
        if (window.confirm('¿Desvincular este gasto del movimiento bancario?')) {
            try {
                await onUnlink();
                toast.success('Gasto desvinculado');
            } catch (e) {
                toast.error('Error desvinculando gasto');
            }
        }
    };

    const fmtPesos = (v) => `$${Math.round(parseFloat(v) || 0).toLocaleString('es-CO')}`;

    const fmtDate = (d) => {
        if (!d) return '';
        const dateStr = typeof d === 'string' ? d.substring(0, 10) : '';
        if (dateStr.length === 10 && dateStr.includes('-')) {
            const [y, m, day] = dateStr.split('-').map(Number);
            const months = ['', 'Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
            return `${day} ${months[m]}`;
        }
        return dateStr;
    };

    // === Vista: Ya vinculado a un gasto ===
    if (!isEditing && currentExpenseId) {
        return (
            <div className="flex items-center space-x-2">
                <div
                    className="text-sm text-orange-700 bg-orange-50 px-3 py-1.5 rounded flex flex-col items-start min-w-[160px] border border-orange-200"
                    title={currentExpenseData ? `${currentExpenseData.expense_provider} - ${fmtPesos(currentExpenseData.expense_amount)}` : `Gasto #${currentExpenseId}`}
                >
                    {currentExpenseData?.expense_provider ? (
                        <>
                            <div className="flex items-center gap-1 mb-0.5">
                                <Receipt className="w-3 h-3 text-orange-500" />
                                <span className="font-bold text-xs text-orange-800 tracking-wide">
                                    {currentExpenseData.expense_provider}
                                </span>
                            </div>
                            <span className="font-medium text-[11px] text-orange-600">
                                {fmtPesos(currentExpenseData.expense_amount)}
                                {currentExpenseData.expense_concept && (
                                    <span className="ml-1 text-orange-500">· {currentExpenseData.expense_concept}</span>
                                )}
                            </span>
                            {currentExpenseData.expense_invoice_number && (
                                <span className="text-[10px] text-orange-400 mt-0.5">
                                    FC: {currentExpenseData.expense_invoice_number}
                                </span>
                            )}
                        </>
                    ) : (
                        <span className="font-medium text-xs text-orange-800">
                            Gasto #{currentExpenseId}
                        </span>
                    )}
                </div>
                <button onClick={handleUnlink} className="text-gray-400 hover:text-red-500 p-1 hover:bg-red-50 rounded" title="Desvincular gasto">
                    <X size={16} />
                </button>
            </div>
        );
    }

    // === Vista: Sin gasto vinculado, botón para abrir búsqueda ===
    if (!isEditing && !currentExpenseId) {
        return (
            <button
                onClick={() => setIsEditing(true)}
                className="text-xs text-orange-400 hover:text-orange-600 flex items-center gap-1 border border-transparent hover:border-orange-200 px-2 py-1 rounded transition-colors"
            >
                <Receipt className="w-3 h-3" />
                + Vincular Gasto
            </button>
        );
    }

    // === Vista: Modo edición con búsqueda ===
    return (
        <div className="relative w-64" ref={wrapperRef}>
            <div className="relative">
                <input
                    type="text"
                    className="w-full text-sm border border-orange-200 rounded pl-2 pr-8 py-1 focus:ring-2 focus:ring-orange-400 focus:border-transparent outline-none bg-orange-50/50"
                    placeholder="Proveedor, concepto, # FC..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    autoFocus
                />
                <button
                    onClick={() => { setIsEditing(false); setSearchTerm(''); }}
                    className="absolute right-1 top-1 text-gray-400 hover:text-gray-600 p-0.5"
                >
                    <X size={14} />
                </button>
            </div>

            {showResults && (
                <div className="absolute z-50 w-80 mt-1 bg-white border border-orange-100 rounded-lg shadow-lg max-h-72 overflow-y-auto text-sm">
                    {loading && (
                        <div className="p-3 text-center text-gray-500 flex items-center justify-center gap-2">
                            <Loader className="w-4 h-4 animate-spin" />
                            Buscando gastos...
                        </div>
                    )}
                    {!loading && results.length === 0 && searchTerm.length >= 2 && (
                        <div className="p-3 text-center text-gray-400">No se encontraron gastos</div>
                    )}
                    {results.map(expense => (
                        <div
                            key={expense.id}
                            className="p-2.5 hover:bg-orange-50 cursor-pointer border-b border-gray-50 last:border-0 transition-colors"
                            onClick={() => handleSelect(expense)}
                        >
                            <div className="flex items-center justify-between">
                                <span className="font-semibold text-gray-800 flex items-center gap-1">
                                    <Receipt className="w-3 h-3 text-orange-400 flex-shrink-0" />
                                    {expense.provider_name || 'Sin proveedor'}
                                </span>
                                <span className="text-sm font-bold text-red-600 flex items-center">
                                    <DollarSign className="w-3 h-3" />
                                    {Math.round(parseFloat(expense.amount) || 0).toLocaleString('es-CO')}
                                </span>
                            </div>
                            <div className="flex items-center justify-between mt-1">
                                <span className="text-xs text-gray-500">
                                    {expense.concept || expense.cost_center || expense.category || 'Sin concepto'}
                                </span>
                                <span className="text-[10px] text-gray-400">
                                    {fmtDate(expense.payment_date || expense.date)}
                                </span>
                            </div>
                            {expense.provider_invoice_number && (
                                <div className="text-[10px] text-gray-400 mt-0.5">
                                    FC: {expense.provider_invoice_number}
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default ExpenseSearchSelect;
